import { afterNextRender, Directive, ElementRef, inject, input, output } from '@angular/core';

/**
 * Loads heavy media only once the host element scrolls near the viewport, while keeping the
 * element in the DOM at its real size from the first render.
 *
 * Unlike `@defer (on viewport)`, the markup is always present (so SSG output has the correct
 * height and fragment/anchor scrolling lands precisely). Only the bytes are deferred:
 *
 * - On a `<video>`, pass `lazyVideoSrc`; the `src` is attached and `load()` is called on entry.
 * - On any other element (e.g. a YouTube wrapper), listen to `(entered)` to render the player.
 */
@Directive({
  selector: '[appLazyViewport]',
})
export class LazyViewportDirective {
  /** Video source to attach lazily. Only used when the host is a `<video>` element. */
  readonly lazyVideoSrc = input('');

  /** How early to start loading before the element reaches the viewport. */
  readonly lazyRootMargin = input('50px');

  /**
   * When the bytes are allowed to start downloading.
   *
   * - `viewport` (default) waits until the element scrolls near the viewport. Right for
   *   anything below the fold.
   * - `idle` waits for `load` and then for the main thread to go idle. Use this for media
   *   that is already in the viewport on arrival — `viewport` would fire immediately and put
   *   a multi-megabyte download in direct competition with the LCP resource. Requires
   *   something else (a poster image) to paint the box in the meantime.
   */
  readonly lazyStrategy = input<'viewport' | 'idle'>('viewport');

  /** Emits once, when the host element first enters the viewport. */
  readonly entered = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    // afterNextRender runs in the browser only, so SSG/SSR never touches IntersectionObserver.
    afterNextRender(() => {
      if (this.lazyStrategy() === 'idle') {
        this.scheduleIdle();
        return;
      }

      this.observe();
    });
  }

  private scheduleIdle(): void {
    const whenIdle = (): void => {
      if (typeof requestIdleCallback === 'function') {
        // The timeout caps the wait on a permanently busy main thread.
        requestIdleCallback(() => this.activate(), { timeout: 2000 });
        return;
      }

      setTimeout(() => this.activate(), 200);
    };

    if (document.readyState === 'complete') {
      whenIdle();
      return;
    }

    window.addEventListener('load', whenIdle, { once: true });
  }

  private observe(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.activate();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        observer.disconnect();
        this.activate();
      },
      { rootMargin: this.lazyRootMargin() },
    );

    observer.observe(this.host.nativeElement);
  }

  private activate(): void {
    const element = this.host.nativeElement;
    const src = this.lazyVideoSrc();

    if (src && element instanceof HTMLVideoElement) {
      element.preload = 'auto';
      element.src = src;
      element.load();
    }

    this.entered.emit();
  }
}
